/**
 * A scripted reading, for working on the journey without buying one.
 *
 * `?onboarding=preview` plays these events on a timer instead of calling
 * brand discovery, DataForSEO and the shop detector — each real run costs
 * money and takes a minute. `&step=goals` (any step) jumps there with the
 * reading already finished, which is how the screenshots are taken.
 *
 * Shaped like the real streams: the same English narration the server sends,
 * the same summary fields, a step that fails.
 */
import type { AnalysisRun } from '../../../common/hooks/use-vendor-analysis-api.hook';
import type { PlatformDetection } from '../../../common/hooks/use-commerce-api.hook';
import { JOURNEY_STEPS, JourneyStep } from './journey';

export const readJourneyPreview = (search: string): { on: boolean; step: JourneyStep | null } => {
    const params = new URLSearchParams(search);
    const on = params.get('onboarding') === 'preview';
    const step = params.get('step') as JourneyStep | null;
    return { on, step: on && step && (JOURNEY_STEPS as readonly string[]).includes(step) ? step : null };
};

const SHOT =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="760">
            <rect width="1200" height="760" fill="#efe9e1"/>
            <rect width="1200" height="84" fill="#fbf8f4"/>
            <rect x="56" y="30" width="150" height="24" rx="4" fill="#3d3a36"/>
            <rect x="760" y="36" width="70" height="12" rx="6" fill="#bdb6ac"/><rect x="850" y="36" width="70" height="12" rx="6" fill="#bdb6ac"/><rect x="940" y="36" width="70" height="12" rx="6" fill="#bdb6ac"/>
            <rect x="0" y="84" width="1200" height="430" fill="#c9b8a3"/>
            <rect x="0" y="330" width="1200" height="184" fill="#a88f74"/>
            <rect x="80" y="170" width="420" height="44" rx="4" fill="#fbf8f4"/>
            <rect x="80" y="232" width="300" height="18" rx="4" fill="#fbf8f4" opacity=".8"/>
            <rect x="80" y="276" width="150" height="40" rx="20" fill="#3d3a36"/>
            <rect x="56" y="560" width="250" height="170" rx="6" fill="#d9cfc2"/><rect x="336" y="560" width="250" height="170" rx="6" fill="#cdbfae"/>
            <rect x="616" y="560" width="250" height="170" rx="6" fill="#e3dbd0"/><rect x="896" y="560" width="250" height="170" rx="6" fill="#bfae98"/>
         </svg>`,
    );

const DISCOVERY: { at: number; event: { type: string; data: any } }[] = [
    { at: 300, event: { type: 'thinking', data: { message: 'Reading https://www.casaceramica.com…' } } },
    { at: 2600, event: { type: 'thinking', data: { message: 'Read 7 pages — 14 colours, 3 fonts, 26 images' } } },
    { at: 3400, event: { type: 'thinking', data: { message: 'Looking at instagram — @casaceramica…' } } },
    { at: 4600, event: { type: 'thinking', data: { message: 'Looking at pinterest — @casaceramica_studio…' } } },
    { at: 5800, event: { type: 'thinking', data: { message: 'Looking at 10 of their pictures…' } } },
    { at: 8200, event: { type: 'thinking', data: { message: 'Kept 6 interior pictures as references.' } } },
    { at: 8800, event: { type: 'thinking', data: { message: 'Working out the brand from what was found…' } } },
    {
        at: 11600,
        event: {
            type: 'tool_result',
            data: {
                name: 'brand_kit_updated',
                brandKit: {
                    brandName: 'Casa Ceramica',
                    primaryColors: ['#3d3a36', '#c9b8a3'],
                    secondaryColors: ['#a88f74', '#efe9e1', '#6e7f6a'],
                    primaryFont: 'Cormorant Garamond',
                    toneOfVoice: 'warm, confident, plain-spoken',
                    styleKeywords: ['mediterranean', 'warm neutrals', 'natural light', 'matte'],
                    valueProposition: 'Handmade Italian and Moroccan tiles, delivered across France in 5 days.',
                    socialHandles: { instagram: 'casaceramica', pinterest: 'casaceramica_studio' },
                },
                report: {
                    profiles: [
                        { platform: 'instagram', handle: 'casaceramica', followers: 12400 },
                        { platform: 'pinterest', handle: 'casaceramica_studio', followers: 3100 },
                    ],
                },
            },
        },
    },
    { at: 11700, event: { type: 'done', data: {} } },
];

const step = (kind: string, status: string) => ({ kind, status }) as any;

const RUNS: { at: number; run: Partial<AnalysisRun> }[] = [
    { at: 500, run: { status: 'running', steps: [step('screenshot', 'running'), step('overview', 'pending'), step('keywords', 'pending'), step('competitors', 'pending'), step('backlinks', 'pending')], summary: {} } },
    { at: 3000, run: { status: 'running', steps: [step('screenshot', 'done'), step('overview', 'running'), step('keywords', 'pending'), step('competitors', 'pending'), step('backlinks', 'pending')], summary: { screenshot: { imageUrl: SHOT } } } },
    {
        at: 6400,
        run: {
            status: 'running',
            steps: [step('screenshot', 'done'), step('overview', 'done'), step('keywords', 'running'), step('competitors', 'pending'), step('backlinks', 'pending')],
            summary: { screenshot: { imageUrl: SHOT }, overview: { organicKeywords: 2751, estimatedMonthlyVisits: 37325, top10: 1109, trafficValue: 16359 } },
        },
    },
    {
        at: 9800,
        run: {
            status: 'running',
            steps: [step('screenshot', 'done'), step('overview', 'done'), step('keywords', 'done'), step('competitors', 'running'), step('backlinks', 'pending')],
            summary: {
                screenshot: { imageUrl: SHOT },
                overview: { organicKeywords: 2751, estimatedMonthlyVisits: 37325, top10: 1109, trafficValue: 16359 },
                keywords: { keywords: [{ keyword: 'carrelage zellige', position: 2, searchVolume: 6600 }] },
            },
        },
    },
    {
        at: 13200,
        run: {
            status: 'succeeded',
            steps: [step('screenshot', 'done'), step('overview', 'done'), step('keywords', 'done'), step('competitors', 'done'), step('backlinks', 'done')],
            summary: {
                screenshot: { imageUrl: SHOT },
                overview: { organicKeywords: 2751, estimatedMonthlyVisits: 37325, top10: 1109, trafficValue: 16359 },
                keywords: { keywords: [{ keyword: 'carrelage zellige', position: 2, searchVolume: 6600 }] },
                competitors: { competitors: [{ domain: 'carrelage-design.fr' }, { domain: 'mosaic-factory.com' }, { domain: 'zellige-maroc.fr' }, { domain: 'lapeyre.fr' }] },
                backlinks: { referringDomains: 312 },
            },
        },
    },
];

const DETECTION: PlatformDetection = {
    platform: 'shopify',
    confidence: 'certain',
    evidence: ['cdn.shopify.com', 'Shopify.theme'],
    connectable: true,
    websiteUrl: 'https://www.casaceramica.com',
    alreadyConnected: false,
};

/**
 * Play the script. `instant` delivers everything at once (a jump straight
 * to a later step). Returns a stop function.
 */
export function playPreview(
    handlers: {
        onDiscovery: (e: { type: string; data: any }) => void;
        onRun: (run: AnalysisRun) => void;
        onDetection: (d: PlatformDetection) => void;
    },
    instant = false,
): () => void {
    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => {
        if (instant) fn();
        else timers.push(window.setTimeout(fn, ms));
    };
    const base = { runId: 'preview', target: 'casaceramica.com', createdAt: new Date().toISOString() } as any;
    for (const d of DISCOVERY) at(d.at, () => handlers.onDiscovery(d.event));
    for (const r of RUNS) at(r.at, () => handlers.onRun({ ...base, ...r.run } as AnalysisRun));
    at(4000, () => handlers.onDetection(DETECTION));
    return () => timers.forEach((t) => window.clearTimeout(t));
}

export const PREVIEW_WEBSITE = 'www.casaceramica.com';
